"use client";

import { useRouter } from "next/navigation";
import { CheckCircle2, AlertTriangle, Info, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";

function relativeDate(ts: number): string {
  const diff = Math.floor(Date.now() / 1000) - ts;
  if (diff < 60) return "À l'instant";
  if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)}h`;
  if (diff < 172800) return "Hier";
  return `Il y a ${Math.floor(diff / 86400)}j`;
}

const typeConfig = {
  success: { icon: CheckCircle2, color: "text-emerald-500", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  warning: { icon: AlertTriangle, color: "text-amber-500", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  info: { icon: Info, color: "text-blue-500", bg: "bg-blue-500/10", border: "border-blue-500/30" },
};

export function ActivityTimeline() {
  const router = useRouter();

  const { data: notifs } = useQuery<{ notifications: Array<{ id: string; type: "success" | "warning" | "info"; title: string; message: string; date: number; link: string }> }>({
    queryKey: ["activity-timeline"],
    queryFn: async () => {
      const res = await fetch("/api/notifications");
      if (!res.ok) return { notifications: [] };
      return res.json();
    },
    staleTime: 60000,
  });

  const events = notifs?.notifications ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Activité récente
        </CardTitle>
      </CardHeader>
      <CardContent>
        {events.length === 0 && (
          <p className="text-sm text-muted-foreground py-3 text-center">Aucune activité récente</p>
        )}
        <div className="relative">
          {events.length > 1 && (
            <div className="absolute left-[15px] top-3 bottom-3 w-px bg-border" />
          )}
          <div className="space-y-3">
            {events.slice(0, 6).map((event) => {
              const config = typeConfig[event.type] || typeConfig.info;
              const Icon = config.icon;
              const dateTs = typeof event.date === "number" ? event.date : Math.floor(new Date(event.date).getTime() / 1000);
              return (
                <button
                  key={event.id}
                  onClick={() => router.push(event.link)}
                  className="flex items-start gap-3 w-full text-left hover:bg-muted/30 rounded-lg p-1.5 -ml-1.5 transition-colors relative"
                >
                  <div className={cn("flex h-7 w-7 shrink-0 items-center justify-center rounded-full", config.bg)}>
                    <Icon className={cn("h-3.5 w-3.5", config.color)} />
                  </div>
                  <div className="flex-1 min-w-0 pt-0.5">
                    <p className="text-sm leading-tight">{event.message}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{relativeDate(dateTs)}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
